/** What to tell the user after a Daily Time import or generation. Pure, so it is easy to test. */
import type { DailyTimeImportSummary, DailyTimeGenerateResult } from '@/stores/dailytime'

export interface Notice {
  message: string
  variant: 'success' | 'warning' | 'danger'
  /** how long to show it, in ms */
  duration: number
}

const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`
const rowList = (rows: number[], max = 6) =>
  rows.length <= max ? rows.join(', ') : `${rows.slice(0, max).join(', ')} and ${rows.length - max} more`
const dateLabel = (key: string) => `${key.slice(4, 6)}/${key.slice(6, 8)}`

export function describeImport(s: DailyTimeImportSummary): Notice {
  if (!s.ok) {
    return { message: `Nothing was imported. ${s.fatal.map((i) => i.message).join(' ')}`, variant: 'danger', duration: 10000 }
  }
  const days = s.created + s.updated
  const parts: string[] = []
  parts.push(
    days || s.unchanged
      ? `Imported ${plural(days, 'day')} (${s.created} new, ${s.updated} updated, ${s.unchanged} already there).`
      : 'No daily times were imported.'
  )
  if (s.skipped.length) {
    const total = s.skipped.reduce((n, r) => n + r.rows.length, 0)
    const shown = s.skipped.slice(0, 4).map((r) => `${r.reason} (row ${rowList(r.rows)})`)
    const more = s.skipped.length > 4 ? ` and ${s.skipped.length - 4} more kind(s)` : ''
    parts.push(`Skipped ${plural(total, 'row')}: ${shown.join('; ')}${more}.`)
  }
  if (s.outsidePeriod) parts.push(`${plural(s.outsidePeriod, 'swipe')} belong to days outside this period and were left out.`)
  if (s.incomplete.length) {
    const shown = s.incomplete.slice(0, 5).map((d) => `${d.id} ${dateLabel(d.date)}`).join(', ')
    const more = s.incomplete.length > 5 ? ` and ${s.incomplete.length - 5} more` : ''
    parts.push(`${plural(s.incomplete.length, 'day')} have a missing clock-in or clock-out and count as no hours until fixed: ${shown}${more}.`)
  }
  if (s.failed.length) parts.push(`${plural(s.failed.length, 'day')} could not be saved (${s.failed.slice(0, 3).map((f) => `${f.id} ${dateLabel(f.date)}`).join(', ')}).`)
  if (s.warnings.length) parts.push(`Check: ${s.warnings.slice(0, 3).join(' ')}`)
  const attention = s.skipped.length || s.outsidePeriod || s.incomplete.length || s.failed.length || s.warnings.length || days + s.unchanged === 0
  return { message: parts.join(' '), variant: s.failed.length || days + s.unchanged === 0 ? 'danger' : attention ? 'warning' : 'success', duration: attention ? 15000 : 5000 }
}

export function describeGenerate(r: DailyTimeGenerateResult): Notice {
  if (!r.ok) {
    return { message: `Daily times were not generated. ${r.issues.map((i) => i.message).join(' ')}`, variant: 'danger', duration: 10000 }
  }
  const parts = [`Generated ${plural(r.created, 'day')}.`]
  if (r.deleted) parts.push(`${plural(r.deleted, 'working day')} replaced.`)
  if (r.kept) parts.push(`${plural(r.kept, 'leave, holiday or other day')} kept as entered.`)
  if (r.failed) parts.push(`${plural(r.failed, 'day')} could not be saved.`)
  return { message: parts.join(' '), variant: r.failed ? 'warning' : 'success', duration: r.failed ? 10000 : 4000 }
}

/** The period's list of imported file names with one more, without repeats or empty entries. */
export function addImportedFile(timelogs: string | null | undefined, filename: string): string {
  const names = String(timelogs ?? '').split(',').map((n) => n.trim()).filter((n) => n && n !== 'undefined')
  if (!names.includes(filename)) names.push(filename)
  return names.join(',')
}
